import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type AnimateOnScrollProps = {
  children: React.ReactNode;
  className?: string;
  variant?: "up" | "right" | "zoom";
  delay?: number; // ms
  once?: boolean;
};

/**
 * Lightweight scroll-reveal component inspired by Elementor effects.
 * Uses IntersectionObserver, no external dependencies.
 */
const AnimateOnScroll: React.FC<AnimateOnScrollProps> = ({
  children,
  className,
  variant = "up",
  delay = 0,
  once = true,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !visible) {
            setVisible(true);
            if (once) obs.disconnect();
          }
        });
      },
      { root: null, rootMargin: "0px 0px -5% 0px", threshold: 0.05 }
    );

    obs.observe(node);
    return () => obs.disconnect();
  }, [once, visible]);

  const initial =
    variant === "right"
      ? "opacity-0 translate-x-2"
      : variant === "zoom"
      ? "opacity-0 scale-[0.65]"
      : "opacity-0 translate-y-2"; // up

  const animated =
    variant === "right"
      ? "animate-slide-in-right"
      : variant === "zoom"
      ? "animate-scale-in"
      : "animate-fade-in"; // up

  const style = visible && delay ? ({ animationDelay: `${delay}ms` } as React.CSSProperties) : undefined;

  return (
    <div
      ref={ref}
      className={cn(
        "motion-reduce:transition-none motion-reduce:animate-none",
        !visible && initial,
        visible && animated,
        className
      )}
      style={{
        ...style,
        contain: 'layout style',
        overflow: 'visible',
      }}
    >
      {children}
    </div>
  );
};

export default AnimateOnScroll;

